import { useState, useEffect, useRef } from 'react'

import { ActionIcon, Collapse, Divider, Loader, Textarea } from '@mantine/core'
import { Check, FileText, Globe, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  BATCH_UPDATE_APPLICATION_QUESTION_RESPONSES,
  SYNC_APPLICATION_QUESTION_RESPONSES,
} from 'src/graphql/customQuestions.graphql'
import {
  CustomQuestionFieldType,
  CustomQuestionVisibility,
} from 'src/graphql/types/customQuestions'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'
import MultiSelectInput from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/MultiSelectInput/MultiSelectInput'
import SelectInput from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/SelectInput/SelectInput'
import TextInput from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/TextInput/TextInput'

const SHORT_TEXT_MAX_LENGTH = 500

interface QuestionResponseLike {
  valueShortText?: string | null
  valueLongText?: string | null
  valueBoolean?: boolean | null
  selectedOptions?: { customQuestionOptionId: string }[] | null
  templateQuestion: {
    order: number
    visibility?: CustomQuestionVisibility | string | null
    question: {
      fieldType: CustomQuestionFieldType | string
    }
  }
}

/**
 * A response counts as answered when its value bucket for the question's
 * field type is filled: non-empty text, non-null boolean (false counts),
 * or at least one selected option.
 */
export const isAnswered = (response: QuestionResponseLike): boolean => {
  switch (response.templateQuestion.question.fieldType) {
    case CustomQuestionFieldType.SHORT_TEXT:
      return !!response.valueShortText
    case CustomQuestionFieldType.LONG_TEXT:
      return !!response.valueLongText
    case CustomQuestionFieldType.BOOLEAN:
      return (
        response.valueBoolean !== null && response.valueBoolean !== undefined
      )
    case CustomQuestionFieldType.SINGLE_SELECT:
    case CustomQuestionFieldType.MULTI_SELECT:
      return (response.selectedOptions?.length ?? 0) > 0
    default:
      return false
  }
}

/**
 * Sort bucket: answered Short form (PUBLIC) questions first, then answered
 * DOT application (PRIVATE) questions, then unanswered questions last.
 */
export const getSourceBucket = (response: QuestionResponseLike): number => {
  if (!isAnswered(response)) return 2
  return response.templateQuestion.visibility ===
    CustomQuestionVisibility.PUBLIC
    ? 0
    : 1
}

/**
 * Comparator for question responses: bucket first (see getSourceBucket),
 * then the template's question order within each bucket.
 */
export const compareQuestionResponses = (
  a: QuestionResponseLike,
  b: QuestionResponseLike
): number => {
  const bucketDiff = getSourceBucket(a) - getSourceBucket(b)
  if (bucketDiff !== 0) return bucketDiff
  return a.templateQuestion.order - b.templateQuestion.order
}

// Matches the activity-log actor pills (see ActivityLogs/shared/ActorBadge)
// so source markers read the same everywhere in the app: same geometry,
// typography, and an icon + label pair. Zap (Auto) and the sparkle (AI) are
// reserved for actor badges, so these use their own silhouettes — a globe for
// the public short form, a document for the full DOT application.
const SOURCE_PILL_BASE =
  'inline-flex items-center gap-0.5 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none'

/**
 * Pill indicating where an answered question was filled out.
 * PUBLIC questions are answered on the public Short form; PRIVATE
 * questions are answered in the DOT application. Unanswered rows get no tag.
 */
const SourceTag = ({ response }: { response: QuestionResponseLike }) => {
  if (!isAnswered(response)) {
    return null
  }

  const isShortForm =
    response.templateQuestion.visibility === CustomQuestionVisibility.PUBLIC

  return (
    <span
      className={`${SOURCE_PILL_BASE} ${
        isShortForm
          ? 'bg-doubleNickel-brand-50 text-doubleNickel-brand-700'
          : 'bg-doubleNickel-gray-100 text-doubleNickel-gray-600'
      }`}
    >
      {isShortForm ? (
        <Globe className="h-2.5 w-2.5" />
      ) : (
        <FileText className="h-2.5 w-2.5" />
      )}
      {isShortForm ? 'Short form' : 'DOT Application'}
    </span>
  )
}

const CompanyQuestionsCard = ({
  applicationId,
  applicantId,
  jobListingId,
  questionResponses = [],
  onUpdate,
}) => {
  const [opened, setOpened] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editedResponses, setEditedResponses] = useState({})
  const hasSyncedRef = useRef(false)
  const currentApplicationRef = useRef(null)

  const [batchUpdateResponses, { loading: updateLoading }] = useMutation(
    BATCH_UPDATE_APPLICATION_QUESTION_RESPONSES,
    {
      onCompleted: () => {
        toast('Responses updated successfully', 'success')
        if (onUpdate) {
          onUpdate()
        }
      },
      onError: (error) => {
        toast(`Unable to update responses: ${error.message}`, 'error')
      },
      refetchQueries: ['ApplicationByApplicantId'],
      awaitRefetchQueries: true,
    }
  )

  const [syncResponses, { loading: syncLoading }] = useMutation(
    SYNC_APPLICATION_QUESTION_RESPONSES,
    {
      onError: (error) => {
        toast(`Unable to sync responses: ${error.message}`, 'error')
      },
      refetchQueries: ['ApplicationByApplicantId'],
      awaitRefetchQueries: true,
    }
  )

  const loading = updateLoading || syncLoading

  // Sync responses once on mount to pick up any newly added template questions.
  // Deps are only the stable ID strings — hasSyncedRef is the sole re-run guard.
  // syncLoading/syncResponses are intentionally omitted: they can change reference
  // on re-render and re-trigger the effect, cascading with refetchQueries.
  useEffect(() => {
    if (currentApplicationRef.current !== applicationId) {
      hasSyncedRef.current = false
      currentApplicationRef.current = applicationId
    }

    if (applicationId && applicantId && jobListingId && !hasSyncedRef.current) {
      hasSyncedRef.current = true
      syncResponses({
        variables: {
          applicationId,
          applicantId,
          jobListingId,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, applicantId, jobListingId])

  // Don't render if no question responses and not loading
  // (While loading, we want to show the card with a loading state)
  if ((!questionResponses || questionResponses.length === 0) && !syncLoading) {
    return null
  }

  // Sort responses by source bucket (answered Short form -> answered DOT
  // application -> unanswered), preserving template order within each bucket
  // (handle empty array during sync)
  const sortedResponses = questionResponses
    ? [...questionResponses].sort(compareQuestionResponses)
    : []

  const handleEdit = () => {
    setEditing(true)
    // Initialize edited responses with current values
    const initialValues = {}
    sortedResponses.forEach((response) => {
      const question = response.templateQuestion.question
      const key = response.applicationQuestionResponseId

      // For select types, get IDs from selectedOptions
      let selectedOptionIds = []
      if (
        question.fieldType === CustomQuestionFieldType.SINGLE_SELECT ||
        question.fieldType === CustomQuestionFieldType.MULTI_SELECT
      ) {
        selectedOptionIds =
          response.selectedOptions?.map((so) => so.customQuestionOptionId) || []
      }

      initialValues[key] = {
        applicationQuestionResponseId: response.applicationQuestionResponseId,
        valueShortText: response.valueShortText,
        valueLongText: response.valueLongText,
        valueBoolean: response.valueBoolean,
        selectedOptionIds,
      }
    })
    setEditedResponses(initialValues)
  }

  const handleCancel = () => {
    setEditing(false)
    setEditedResponses({})
  }

  const hasValidationErrors = () => {
    return Object.entries(editedResponses).some(([responseId, values]) => {
      const response = sortedResponses.find(
        (r) => r.applicationQuestionResponseId === responseId
      )
      return (
        response &&
        response.templateQuestion.question.fieldType ===
          CustomQuestionFieldType.SHORT_TEXT &&
        values.valueShortText &&
        values.valueShortText.length > SHORT_TEXT_MAX_LENGTH
      )
    })
  }

  const handleSave = async () => {
    try {
      // Build updates for all edited responses
      const updates = Object.entries(editedResponses).map(([_, values]) => ({
        id: values.applicationQuestionResponseId,
        valueShortText: values.valueShortText,
        valueLongText: values.valueLongText,
        valueBoolean: values.valueBoolean,
        selectedOptionIds: values.selectedOptionIds,
      }))

      // Update all responses with user's edited values
      if (updates.length > 0) {
        await batchUpdateResponses({
          variables: {
            updates,
          },
        })
      }

      setEditing(false)
      setEditedResponses({})
    } catch (error) {
      console.error('Error saving responses:', error)
    }
  }

  const updateResponseValue = (responseId, field, value) => {
    setEditedResponses((prev) => ({
      ...prev,
      [responseId]: {
        ...prev[responseId],
        [field]: value,
      },
    }))
  }

  const renderAnswer = (response) => {
    const responseKey = response.applicationQuestionResponseId
    const {
      templateQuestion,
      valueShortText,
      valueLongText,
      valueBoolean,
      selectedOptions,
    } = response
    const question = templateQuestion.question

    // Get selected option IDs from selectedOptions relation
    const selectedOptionIds =
      selectedOptions?.map((so) => so.customQuestionOptionId) || []

    // Get current value from edited state or original response
    const currentValue = editedResponses[responseKey] || {
      applicationQuestionResponseId: response.applicationQuestionResponseId,
      valueShortText,
      valueLongText,
      valueBoolean,
      selectedOptionIds,
    }

    if (!editing) {
      // Read-only view: display value plus a source tag on answered questions
      let displayValue: string
      switch (question.fieldType) {
        case CustomQuestionFieldType.SHORT_TEXT:
          displayValue = valueShortText || '-'
          break

        case CustomQuestionFieldType.LONG_TEXT:
          displayValue = valueLongText || '-'
          break

        case CustomQuestionFieldType.BOOLEAN:
          displayValue =
            valueBoolean !== null && valueBoolean !== undefined
              ? valueBoolean
                ? 'Yes'
                : 'No'
              : '-'
          break

        case CustomQuestionFieldType.SINGLE_SELECT: {
          const selectedOption =
            selectedOptions && selectedOptions.length > 0
              ? selectedOptions[0].option
              : null
          displayValue = selectedOption?.label || '-'
          break
        }

        case CustomQuestionFieldType.MULTI_SELECT: {
          const selectedLabels =
            selectedOptions?.map((so) => so.option.label) || []
          displayValue =
            selectedLabels.length > 0 ? selectedLabels.join(', ') : '-'
          break
        }

        default:
          return (
            <div className="text-sm italic text-doubleNickel-gray-400">
              Unknown question type
            </div>
          )
      }

      return (
        <div className="text-sm text-doubleNickel-gray-900">
          <span
            className={
              question.fieldType === CustomQuestionFieldType.LONG_TEXT
                ? 'whitespace-pre-wrap'
                : undefined
            }
          >
            {displayValue}
          </span>
        </div>
      )
    }

    // Editing mode
    switch (question.fieldType) {
      case CustomQuestionFieldType.SHORT_TEXT:
        return (
          <TextInput
            editing={editing}
            value={currentValue.valueShortText || ''}
            placeholder={question.placeholder || 'Enter your answer'}
            handleChange={(value) =>
              updateResponseValue(responseKey, 'valueShortText', value)
            }
            error={
              currentValue.valueShortText &&
              currentValue.valueShortText.length > SHORT_TEXT_MAX_LENGTH
                ? `Maximum ${SHORT_TEXT_MAX_LENGTH} characters allowed`
                : undefined
            }
          />
        )

      case CustomQuestionFieldType.LONG_TEXT:
        return (
          <Textarea
            value={currentValue.valueLongText || ''}
            placeholder={question.placeholder || 'Enter your answer'}
            rows={4}
            onChange={(e) =>
              updateResponseValue(responseKey, 'valueLongText', e.target.value)
            }
            classNames={{
              input: 'text-sm',
            }}
          />
        )

      case CustomQuestionFieldType.BOOLEAN:
        return (
          <SelectInput
            editing={editing}
            value={
              currentValue.valueBoolean === null ||
              currentValue.valueBoolean === undefined
                ? null
                : currentValue.valueBoolean.toString()
            }
            handleChange={(value) => {
              updateResponseValue(
                responseKey,
                'valueBoolean',
                value === null ? null : value === 'true'
              )
            }}
            data={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            placeholder="Select an option"
            clearable
          />
        )

      case CustomQuestionFieldType.SINGLE_SELECT:
        return (
          <SelectInput
            editing={editing}
            value={
              currentValue.selectedOptionIds &&
              currentValue.selectedOptionIds.length > 0
                ? currentValue.selectedOptionIds[0]
                : null
            }
            handleChange={(value) => {
              updateResponseValue(
                responseKey,
                'selectedOptionIds',
                value ? [value] : []
              )
            }}
            data={question.options.map((option) => ({
              value: option.customQuestionOptionId,
              label: option.label,
            }))}
            placeholder={question.placeholder || 'Select an option'}
            clearable
          />
        )

      case CustomQuestionFieldType.MULTI_SELECT:
        return (
          <MultiSelectInput
            editing={editing}
            value={currentValue.selectedOptionIds || []}
            handleChange={(values) => {
              updateResponseValue(responseKey, 'selectedOptionIds', values)
            }}
            data={question.options.map((option) => ({
              value: option.customQuestionOptionId,
              label: option.label,
            }))}
            placeholder={question.placeholder || 'Select options'}
            searchable
            clearable
          />
        )

      default:
        return (
          <span className="italic text-doubleNickel-gray-400">
            Unknown question type
          </span>
        )
    }
  }

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      <div
        className="text-md flex w-full cursor-pointer flex-row items-center gap-2 font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Questions
        {!editing ? (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        ) : (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading || hasValidationErrors()}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
            {loading && <Loader size={12} color="gray" />}
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col px-4">
            {sortedResponses.length === 0 && syncLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader size="sm" color="gray" />
                <span className="ml-2 text-sm text-doubleNickel-gray-500">
                  Loading questions...
                </span>
              </div>
            ) : (
              sortedResponses.map((response, index) => {
                const question = response.templateQuestion.question
                const isRequired = response.templateQuestion.isRequired

                return (
                  <div key={response.applicationQuestionResponseId}>
                    <div className="grid grid-cols-2 items-start gap-4 py-3">
                      <div className="text-sm font-medium text-doubleNickel-gray-700">
                        {question.label}
                        {isRequired && (
                          <span className="ml-1 text-doubleNickel-error-500">
                            *
                          </span>
                        )}
                        <span className="ml-2 align-middle">
                          <SourceTag response={response} />
                        </span>
                      </div>
                      <div>{renderAnswer(response)}</div>
                    </div>
                    {index < sortedResponses.length - 1 && (
                      <Divider color="gray.2" />
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default CompanyQuestionsCard
